import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { pdf } from '@react-pdf/renderer'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import FormControlLabel from '@mui/material/FormControlLabel'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useMediaQuery, useTheme } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import API from '../../../../../services/API'
import BigModal from '../../../../../components/Modals/BigModal'
import { GlobalContext, TenantSettingsContext, UserContext } from '../../../../../App'
import { uploadDocument } from '../../../../../helpers/uploadDocument'
import {
  ChangePresentContext,
  EventMetaContext,
  PostEventAnalysisContext,
} from '../../SingleEvent'
import {
  REPORT_SECTIONS,
  SECTION_GROUPS,
  availability,
  buildInclude,
  defaultSelection,
  mergeSelection,
  partKey,
} from './sections'
import { renderReportCharts } from './reportCharts'
import PostEventReportPDF from './PostEventReportPDF'

// Generator for the Post Event Report. The planner picks the sections and,
// within each, the parts to print, seeing each part's figure first so a weak
// number can be left out on its own. The PDF is built in the browser, always
// saved to the event's Documents, then previewed.

const storageKey = (eventID) => `postEventReport:${eventID}`

const readSaved = (eventID) => {
  try {
    const raw = window.localStorage.getItem(storageKey(eventID))
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const writeSaved = (eventID, value) => {
  try {
    window.localStorage.setItem(storageKey(eventID), JSON.stringify(value))
  } catch {
    // Storage unavailable (private window); the defaults apply next time
  }
}

// ASCII-only, since the S3 Content-Disposition header is Latin-1
const fileSafe = (value) =>
  String(value || 'Event')
    .normalize('NFKD')
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/[^A-Za-z0-9 _-]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 60) || 'Event'

// A summary should never break the picker over one odd record
const safely = (fn, ...args) => {
  try {
    return fn ? fn(...args) : null
  } catch {
    return null
  }
}

const PartRow = ({ section, part, data, pea, selection, reason, sectionOn, onToggle }) => {
  const disabled = !!reason || !sectionOn
  const summary = reason ? null : safely(part.summary, data, pea, selection)
  const flag = reason ? null : safely(part.flag, data, pea)
  return (
    <Stack direction="row" alignItems="center" gap={1} sx={{ minHeight: 32, opacity: disabled ? 0.55 : 1 }}>
      <Checkbox
        size="small"
        sx={{ p: 0.5 }}
        disabled={disabled}
        checked={!reason && selection.parts[partKey(section.key, part.key)] !== false}
        onChange={(e) => onToggle(partKey(section.key, part.key), e.target.checked)}
        inputProps={{ 'aria-label': part.label }}
      />
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
        {part.label}
      </Typography>
      {flag && (
        <Tooltip title={flag} arrow>
          <Chip label="Check" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
        </Tooltip>
      )}
      <Typography
        variant="caption"
        fontWeight={reason ? 400 : 700}
        color={reason ? 'text.secondary' : 'text.primary'}
        sx={{ textAlign: 'right', maxWidth: '55%' }}
      >
        {reason || summary || ''}
      </Typography>
    </Stack>
  )
}

const SectionCard = ({ section, data, pea, selection, reasons, onToggleSection, onTogglePart, children }) => {
  const reason = reasons[section.key]
  const on = !reason && !!selection.sections[section.key]
  const available = section.parts.filter((p) => !reasons[partKey(section.key, p.key)])
  const chosen = available.filter((p) => selection.parts[partKey(section.key, p.key)] !== false)
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, opacity: reason ? 0.6 : 1 }}>
      <Stack direction="row" alignItems="flex-start" gap={1}>
        <Checkbox
          size="small"
          sx={{ p: 0.5, mt: -0.25 }}
          disabled={!!reason}
          checked={on}
          onChange={(e) => onToggleSection(section.key, e.target.checked)}
          inputProps={{ 'aria-label': section.label }}
        />
        <Stack flex={1} minWidth={0}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Typography variant="body2" fontWeight={700}>
              {section.label}
            </Typography>
            {on && section.parts.length > 1 && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {chosen.length} of {available.length} parts
              </Typography>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {reason || section.description}
          </Typography>
        </Stack>
      </Stack>
      <Collapse in={on} unmountOnExit>
        <Stack sx={{ mt: 1, pl: 0.5 }} divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
          {section.parts
            .filter((part) => !(part.hideWhenUnavailable && reasons[partKey(section.key, part.key)]))
            .map((part) => (
              <PartRow
                key={part.key}
                section={section}
                part={part}
                data={data}
                pea={pea}
                selection={selection}
                reason={reasons[partKey(section.key, part.key)]}
                sectionOn={on}
                onToggle={onTogglePart}
              />
            ))}
        </Stack>
        {children}
      </Collapse>
    </Paper>
  )
}

const GenerateReport = () => {
  const { eventMeta } = useContext(EventMetaContext)
  const { postEventAnalysis, setPostEventAnalysis } = useContext(PostEventAnalysisContext)
  const { changesPresent, setChangesPresent } = useContext(ChangePresentContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const eventID = eventMeta._id

  const [data, setData] = useState(null)
  const [loadError, setLoadError] = useState(false)
  const [selection, setSelection] = useState(() => mergeSelection(readSaved(eventID)?.selection))
  const [stage, setStage] = useState(null) // null | 'building' | 'saving'
  const [preview, setPreview] = useState(null) // { url, fileName }

  const load = useCallback(async () => {
    setLoadError(false)
    const res = await API.get(`/events/${eventID}/post-event-report`)
    if (res.status !== 200) {
      setLoadError(true)
      return null
    }
    setData(res.data)
    return res.data
  }, [eventID])

  useEffect(() => {
    if (eventID) load()
  }, [eventID, load])

  useEffect(() => {
    writeSaved(eventID, { selection })
  }, [eventID, selection])

  useEffect(() => () => preview?.url && URL.revokeObjectURL(preview.url), [preview])

  const reasons = useMemo(
    () => (data ? availability(data, postEventAnalysis) : {}),
    [data, postEventAnalysis],
  )

  const include = useMemo(
    () => (data ? buildInclude(data, postEventAnalysis, selection) : null),
    [data, postEventAnalysis, selection],
  )

  const toggleSection = (key, checked) =>
    setSelection((prev) => ({ ...prev, sections: { ...prev.sections, [key]: checked } }))
  const togglePart = (key, checked) =>
    setSelection((prev) => ({ ...prev, parts: { ...prev.parts, [key]: checked } }))

  const handleGenerate = async () => {
    setStage('building')
    try {
      // Fresh figures, so records finished since the tab opened are counted
      const fresh = (await load()) || data
      if (!fresh) throw new Error('Unable to load the report data.')
      const generatedAt = new Date()
      const chrome = {
        orgName: orgSettings?.orgName || '',
        logo: orgSettings?.logo || null,
        docLabel: 'Post Event Report',
        title: fresh.event.title || '',
        generatedAt,
        generatedBy: [user?.firstName, user?.lastName].filter(Boolean).join(' '),
      }
      // Worked out again against the fresh figures
      const freshInclude = buildInclude(fresh, postEventAnalysis, selection)
      const charts = renderReportCharts(fresh, { byDay: freshInclude.part('days', 'pages') })
      const blob = await pdf(
        <PostEventReportPDF
          data={fresh}
          pea={postEventAnalysis}
          include={freshInclude}
          charts={charts}
          chrome={chrome}
        />,
      ).toBlob()

      const version = fresh.event.reportVersion
      const fileName = `Post_Event_Report_v${version}_${fileSafe(fresh.event.title)}.pdf`
      setPreview({ url: URL.createObjectURL(blob), fileName })

      // Every generated report is kept on the event as a record of what was issued
      setStage('saving')
      try {
        const file = new File([blob], fileName, { type: 'application/pdf' })
        const s3key = await uploadDocument(file, 'events/documents')
        const res = await API.post('/event/add-document', {
          eventID,
          documentName: `Post Event Report - v${version} - ${moment(generatedAt).format('D MMM YYYY')}`,
          visibleToAllStaff: false,
          s3key,
        })
        if (res.status !== 200) throw new Error('Unable to save the report to the event.')
        window.dispatchEvent(
          new CustomEvent('ambunet:event-document-added', {
            detail: {
              eventID,
              document: { ...res.data, author: { firstName: user?.firstName, lastName: user?.lastName } },
            },
          }),
        )
        setToast({ message: `Report v${version} saved to the event's documents` })
        // Next report gets the next version number
        load()
      } catch (e) {
        setToast({
          message: `${e?.message || 'Unable to save the report.'} You can still download it.`,
          severity: 'error',
        })
      }
    } catch (e) {
      console.error(e)
      setToast({ message: e?.message || 'Unable to generate the report.', severity: 'error' })
    } finally {
      setStage(null)
    }
  }

  const draftRecords = data?.completeness?.draftRecords || 0
  const selectedCount = include ? REPORT_SECTIONS.filter((s) => include[s.key]).length : 0

  return (
    <Stack gap={2}>
      <Stack gap={0.5}>
        <Typography variant="h6" fontWeight={800}>
          Post Event Report
        </Typography>
        <Typography variant="body2" color="text.secondary">
          A professional report for the event organiser and Safety Advisory Group. Patient
          information is shown as totals only. Each report is saved to this event's documents.
        </Typography>
      </Stack>

      {loadError && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          Unable to load the report data.
        </Alert>
      )}

      {!data && !loadError ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={28} />
        </Box>
      ) : null}

      {data && (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography variant="body2" color="text.secondary">
              Tick the sections and figures to include. Each figure is shown as it will appear;
              anything marked <strong>Check</strong> may be worth a look before it goes to the client.
            </Typography>
            <Button size="small" color="inherit" onClick={() => setSelection(defaultSelection())} sx={{ flexShrink: 0 }}>
              Reset to defaults
            </Button>
          </Stack>

          {SECTION_GROUPS.map((group) => (
            <Stack key={group} gap={1}>
              <Typography variant="overline" color="text.secondary" fontWeight={700} lineHeight={1.5}>
                {group}
              </Typography>
              <Grid container spacing={1.5}>
                {REPORT_SECTIONS.filter((s) => s.group === group).map((s) => (
                  <Grid item xs={12} lg={6} key={s.key}>
                    <SectionCard
                      section={s}
                      data={data}
                      pea={postEventAnalysis}
                      selection={selection}
                      reasons={reasons}
                      onToggleSection={toggleSection}
                      onTogglePart={togglePart}
                    >
                      {/* Only the day pages use it, so only offered when there are day pages */}
                      {s.key === 'clinical' && !reasons.days && (
                        <Stack direction="row" alignItems="center" gap={1.5} sx={{ mt: 1.5, pl: 0.5 }} flexWrap="wrap">
                          <Typography variant="caption" color="text.secondary">
                            On each day&apos;s page, show presenting complaints as
                          </Typography>
                          <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={selection.complaintView}
                            onChange={(e, value) =>
                              value && setSelection((prev) => ({ ...prev, complaintView: value }))
                            }
                          >
                            <ToggleButton value="category" sx={{ py: 0.25, px: 1.25, textTransform: 'none' }}>
                              Categories
                            </ToggleButton>
                            <ToggleButton value="individual" sx={{ py: 0.25, px: 1.25, textTransform: 'none' }}>
                              Categories and each complaint
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Stack>
                      )}
                    </SectionCard>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          ))}

          <FormControlLabel
            control={
              <Switch
                checked={!!postEventAnalysis?.peaComplete}
                onChange={(e) => {
                  setPostEventAnalysis((prev) => ({ ...prev, peaComplete: e.target.checked }))
                  !changesPresent && setChangesPresent(true)
                }}
                color="success"
              />
            }
            label="Mark PEA as complete"
          />

          {draftRecords > 0 && (
            <Alert severity="warning">
              {draftRecords} patient record{draftRecords === 1 ? ' is' : 's are'} still in draft. They
              will be included, but their details may change once completed.
            </Alert>
          )}
          {changesPresent && (
            <Alert severity="info">
              The report uses the PEA text as it is on screen now, including changes you haven't saved yet.
            </Alert>
          )}
          {isMobile && (
            <Alert severity="info">Reports can only be generated on a desktop or tablet.</Alert>
          )}

          <Button
            color="success"
            variant="contained"
            size="large"
            fullWidth
            startIcon={stage ? <CircularProgress size="1rem" color="inherit" /> : <PictureAsPdfIcon />}
            disabled={isMobile || !!stage || selectedCount === 0}
            onClick={handleGenerate}
          >
            {stage === 'building'
              ? 'Building the report'
              : stage === 'saving'
                ? 'Saving to the event'
                : `Generate report v${data.event.reportVersion}`}
          </Button>
        </>
      )}

      <BigModal open={!!preview} onClose={() => setPreview(null)} style={{ height: 600 }}>
        {preview && (
          <Stack gap={1} height="100%" minHeight="80vh">
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                component="a"
                href={preview.url}
                download={preview.fileName}
              >
                Download
              </Button>
            </Stack>
            <Box component="iframe" title="Post Event Report" src={preview.url} sx={{ flex: 1, border: 0, minHeight: '75vh' }} />
          </Stack>
        )}
      </BigModal>
    </Stack>
  )
}

export default GenerateReport
